import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import {
  BOOKING_PAGE_URL,
  EVENT_PAGE_URL,
  REVIEWS_PAGE_URL,
} from "../../utilities/constants/PagesEndpoints";
import ReviewCard from "../reviews/ReviewCard";
import { EntitiesListResponseModel } from "../../utilities/models/EntitiesListResponseModel";
import { getReviews } from "../reviews/ReviewService";
import { getAllEvents } from "../event/EventServices";
import EventPreviewCard from "../event/EventPreviewCard";
import { NavLink } from "react-router-dom";
import ServicesOverview from "./ServicesOverview";
import CallToAction from "./CallToAction";
import { ArrowRight, ChevronRight } from "lucide-react";

const HomePage = () => {
  const navigateTo = useNavigate();
  const [eventsData, setEventsData] = useState(EntitiesListResponseModel);
  const [reviewsData, setReviewsData] = useState(EntitiesListResponseModel);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const reviewsSample = reviewsData.content
    .filter((review) => review.rating >= 3)
    .slice(0, 10);

  useEffect(() => {
    getReviews(setReviewsData, { sortDirection: "DESC", sortBy: "postedDate" });
    getAllEvents(setEventsData, {
      sortDirection: "DESC",
      sortBy: "postedDate",
    });
  }, []);

  // Lazy load video after component mounts
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Video will load from source elements
      video.load();
    }
  }, []);

  return (
    <div className="bg-white dark:bg-slate-950 font-sans">
      {/* --- HERO SECTION --- */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          {/* Poster/Fallback Image - shown while video loads */}
          <div
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${videoLoaded ? "opacity-0" : "opacity-100"}`}
            style={{
              backgroundImage: "url('/assets/images/about_us_cover.png')",
            }}
          />

          {/* Optimized Video with WebM + MP4 sources */}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            poster="/assets/images/about_us_cover.png"
            onCanPlay={() => setVideoLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
          >
            {/* WebM for modern browsers (better compression) */}
            <source
              src="/assets/videos/home_cover_page.webm"
              type="video/webm"
            />
            {/* MP4 fallback for Safari and older browsers */}
            <source src="/assets/videos/home_cover_page.mp4" type="video/mp4" />
          </video>

          {/* Gradients overlays for readability */}
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative h-full container mx-auto px-6 flex flex-col justify-center items-start pt-20">
          <div className="max-w-4xl space-y-6 animate-fade-in-up">
            <span className="inline-block px-3 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-full text-sm font-bold tracking-widest uppercase backdrop-blur-sm">
              Premium Event Venue
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif text-white leading-tight">
              Step into <span className="text-yellow-500 italic">Elegance</span>
              ,<br />
              Celebrate in Style.
            </h1>
            <p className="text-lg md:text-2xl text-slate-200 max-w-2xl font-light">
              Le Prestige Hall offers the perfect canvas for your most cherished
              moments. Experience luxury, comfort, and unforgettable memories.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <NavLink
                to={BOOKING_PAGE_URL}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="px-8 py-4 bg-blue-600 text-white rounded-full font-bold text-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-600/30"
              >
                Plan Your Event <ArrowRight size={20} />
              </NavLink>
              <button
                onClick={() =>
                  document
                    .getElementById("services-section")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="px-8 py-4 bg-white/10 text-white border border-white/30 rounded-full font-bold text-lg hover:bg-white/20 transition-all backdrop-blur-sm"
              >
                Explore Venue
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --- SERVICES SECTION --- */}
      <div id="services-section">
        <ServicesOverview />
      </div>

      {/* --- LATEST EVENTS SECTION --- */}
      <section className="py-20 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white font-serif mb-2">
                Detailed Reality
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                See how others brought their vision to life.
              </p>
            </div>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                navigateTo(EVENT_PAGE_URL);
              }}
              className="hidden md:flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold hover:gap-3 transition-all"
            >
              View Gallery <ArrowRight size={20} />
            </button>
          </div>

          {/* Scroll Container */}
          <div className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory no-scrollbar -mx-6 px-6 md:mx-0 md:px-0">
            {eventsData.content.slice(0, 6).map((event) => (
              <div
                key={event.id}
                className="min-w-[85vw] md:min-w-[350px] snap-center"
              >
                <EventPreviewCard event={event} />
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigateTo(EVENT_PAGE_URL);
            }}
            className="mt-8 w-full md:hidden flex items-center justify-center gap-2 px-6 py-3 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium"
          >
            View All Events
          </button>
        </div>
      </section>

      {/* --- REVIEWS SECTION --- */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white font-serif mb-2">
                Loved by Locals
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                Don't just take our word for it.
              </p>
            </div>
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
                navigateTo(REVIEWS_PAGE_URL);
              }}
              className="hidden md:flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold hover:gap-3 transition-all"
            >
              Read All Reviews <ArrowRight size={20} />
            </button>
          </div>

          {/* Scroll Container */}
          <div className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory no-scrollbar -mx-6 px-6 md:mx-0 md:px-0">
            {reviewsSample.map((review) => (
              <div
                key={review.id}
                className="min-w-[85vw] md:min-w-[400px] snap-center"
              >
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigateTo(REVIEWS_PAGE_URL);
            }}
            className="mt-8 w-full md:hidden flex items-center justify-center gap-2 px-6 py-3 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-300 font-medium"
          >
            Read All Reviews
          </button>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <CallToAction />
    </div>
  );
};

export default HomePage;
